/*
  MIT License http://www.opensource.org/licenses/mit-license.php
  Author Tobias Koppers @sokra, Zackary Jackson @ScriptedAlchemy
*/
import { normalizeWebpackPath } from '@module-federation/sdk/normalize-webpack-path';

import type { Module, ChunkGraph, Compilation, Chunk } from 'webpack';
import ConsumeSharedModule from './ConsumeSharedModule';
import { getFederationGlobalScope } from '../container/runtime/utils';

const { Template, RuntimeGlobals, RuntimeModule } = require(
  normalizeWebpackPath('webpack'),
) as typeof import('webpack');

class ConsumeSharedRuntimeModule extends RuntimeModule {
  private _runtimeRequirements: ReadonlySet<string>;

  /**
   * @param {ReadonlySet<string>} runtimeRequirements runtime requirements
   */
  constructor(runtimeRequirements: ReadonlySet<string>) {
    super('consumes', RuntimeModule.STAGE_ATTACH);
    this._runtimeRequirements = runtimeRequirements;
  }

  /**
   * @returns {string | null} runtime code
   */
  override generate(): string | null {
    const compilation: Compilation = this.compilation!;
    const chunkGraph: ChunkGraph = this.chunkGraph!;
    const { runtimeTemplate, codeGenerationResults } = compilation;
    if (!codeGenerationResults) {
      return null;
    }
    const chunkToModuleMapping: Record<string, any> = {};
    const moduleIdToSourceMapping: Map<string | number, string> = new Map();
    const initialConsumes: (string | number)[] = [];
    /**
     *
     * @param {Iterable<Module>} modules modules
     * @param {Chunk} chunk the chunk
     * @param {(string | number)[]} list list of ids
     */
    const addModules = (
      modules: Iterable<Module>,
      chunk: Chunk,
      list: (string | number)[],
    ) => {
      for (const m of modules) {
        const module: ConsumeSharedModule = m as unknown as ConsumeSharedModule;
        // @ts-ignore
        const id = chunkGraph.getModuleId(module);
        if (id === null || id === undefined) {
          continue;
        }
        list.push(id);
        const moduleGetter = codeGenerationResults.getSource(
          // @ts-ignore
          module,
          chunk.runtime,
          'consume-shared',
        );
        if (!moduleGetter) {
          continue;
        }
        const shareOption = codeGenerationResults.getData(
          // @ts-ignore
          module,
          chunk.runtime,
          'consume-shared',
        );
        if (!shareOption) {
          continue;
        }
        moduleIdToSourceMapping.set(
          id,
          Template.asString([
            '{',
            Template.indent([
              `fallback: ${moduleGetter.source().toString()},`,
              `shareScope: ${JSON.stringify(
                Array.isArray(shareOption.shareScope)
                  ? shareOption.shareScope
                  : [shareOption.shareScope || 'default'],
              )},`,
              `singleton: ${JSON.stringify(shareOption.shareConfig.singleton)},`,
              `requiredVersion: ${JSON.stringify(shareOption.shareConfig.requiredVersion)},`,
              `strictVersion: ${JSON.stringify(shareOption.shareConfig.strictVersion)},`,
              `eager: ${JSON.stringify(shareOption.shareConfig.eager)},`,
              `layer: ${JSON.stringify(shareOption.shareConfig.layer)},`,
              `shareKey: "${shareOption.shareKey}",`,
              `${shareOption.treeShakingMode ? `treeShakingMode: ${JSON.stringify(shareOption.treeShakingMode)},` : ''}`,
            ]),
            '}',
          ]),
        );
      }
    };

    const allChunks = [...(this.chunk?.getAllReferencedChunks() || [])];
    for (const chunk of allChunks) {
      const modules = chunkGraph.getChunkModulesIterableBySourceType(
        chunk,
        'consume-shared',
      );
      if (!modules) continue;
      // chunk.id may equal 0
      if (chunk.id === null || (typeof chunk.id === 'string' && !chunk.id))
        continue;

      addModules(
        modules,
        chunk,
        (chunkToModuleMapping[chunk.id.toString()] = []),
      );
    }
    for (const chunk of [...(this.chunk?.getAllInitialChunks() || [])]) {
      const modules = chunkGraph.getChunkModulesIterableBySourceType(
        chunk,
        'consume-shared',
      );
      if (!modules) continue;
      addModules(modules, chunk, initialConsumes);
    }

    if (moduleIdToSourceMapping.size === 0) return null;

    const federationGlobal = getFederationGlobalScope(RuntimeGlobals);

    return Template.asString([
      'var installedModules = {};',
      `${RuntimeGlobals.require}.consumesLoadingData.moduleIdToConsumeDataMapping = {`,
      Template.indent(
        Array.from(moduleIdToSourceMapping, ([key, value]) => {
          return `${JSON.stringify(key)}: ${value}`;
        }).join(',\n'),
      ),
      '};',

      `var moduleToHandlerMapping = {};`,

      initialConsumes.length > 0
        ? Template.asString([
            `${RuntimeGlobals.require}.consumesLoadingData.initialConsumes = ${JSON.stringify(initialConsumes)};`,
            `${federationGlobal}.installInitialConsumes = ${runtimeTemplate.returningFunction(
              Template.asString([
                `${federationGlobal}.bundlerRuntime.installInitialConsumes({`,
                Template.indent([
                  `initialConsumes: ${RuntimeGlobals.require}.consumesLoadingData.initialConsumes,`,
                  'installedModules:installedModules,',
                  `moduleToHandlerMapping,`,
                  `webpackRequire: ${RuntimeGlobals.require},`,
                  `asyncLoad: typeof options==='object' ? options.asyncLoad : undefined,`,
                ]),
                `})`,
              ]),
              'options',
            )}`,
          ])
        : '// no consumes in initial chunks',
      this._runtimeRequirements.has(RuntimeGlobals.ensureChunkHandlers)
        ? Template.asString([
            `${RuntimeGlobals.require}.consumesLoadingData.chunkMapping = ${JSON.stringify(
              chunkToModuleMapping,
              null,
              '\t',
            )};`,
            `${
              RuntimeGlobals.ensureChunkHandlers
            }.consumes = ${runtimeTemplate.basicFunction('chunkId, promises', [
              `${federationGlobal}.bundlerRuntime.consumes({`,
              `chunkMapping: ${RuntimeGlobals.require}.consumesLoadingData.chunkMapping,`,
              'installedModules: installedModules,',
              'chunkId: chunkId,',
              `moduleToHandlerMapping,`,
              'promises: promises,',
              `webpackRequire:${RuntimeGlobals.require}`,
              '});',
            ])}`,
          ])
        : '// no chunk loading of consumes',
    ]);
  }
}

export default ConsumeSharedRuntimeModule;
